// Package imports:
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Bugsnag from "@bugsnag/js";
// Component imports:
import PepWatchMonitor from "./PepWatchMonitor";
import LockIcon from "../../ui-elements/LockIcon/LockIcon";
import Alert from "../../ui-elements/Alert/Alert";
// Service imports:
import { isSubscriptionValid } from "../../services/utils";
import { GET_KELDAN_API_URL } from "../../services/config";
// Type imports:
import { IKeldanApiResponse, Subscription } from "../../types/KeldanTypes";
import { PepWatchResponse, PepWatchMonitorData } from "../../types/PepTypes";
import PepWatchTableSection from "./PepWatchTableSection";
import Link from "../../ui-elements/Link/Link";

export interface IPepWatchResponse<T = any> {
  result: number;
  descr: string;
  data: T;
  failed?: T[];
  count?: number;
}
interface IProps {
  isAuthenticated: boolean;
  subscriptions: Subscription[] | null;
}

export interface IForeignSearchParams {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: string;
}

const PepWatch: React.FC<IProps> = ({ isAuthenticated, subscriptions }) => {
  const [monitorData, setMonitorData] = useState<PepWatchMonitorData | undefined>(undefined);

  // Create a fetchData function that can be called both initially and for refetching
  const fetchData = useCallback(async () => {
    try {
      const url = `${GET_KELDAN_API_URL()}/Pep/GetPepWatchMonitorData`;
      const headers = {
        "Content-Type": "application/json; charset=utf-8",
      };
      const response = await fetch(url, {
        method: "GET",
        headers,
      });
      const responseBody: IKeldanApiResponse<PepWatchResponse<PepWatchMonitorData>> = await response.json();

      setMonitorData(responseBody.result?.data);
    } catch (e) {
      if( e instanceof Error) {
        Bugsnag.notify(e)
      }
      setMonitorData(undefined);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    !isDisabled && fetchData();
    setMonitorData(undefined); // Reset monitor data on mount
  }, [fetchData]);

  const isDisabled = useMemo(() => {
    return !isSubscriptionValid(subscriptions, isAuthenticated, 35);
  }, [isAuthenticated, subscriptions]);

  return (
    <>
      <div className="KCL_pep-search pep_watch shell">
        <div className="main__inner-fluid">
          <div className="upperSection">
            <div className="descriptionWrapper">
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
                {isDisabled && <LockIcon size="lg" />}
                <h3>Vöktun á stjórnmálalegum tengslum</h3>
              </div>

              <div>
                <p className="paragraph">
                  Tilkynningarskyldum aðilum er skylt að kanna stjórnmálaleg tengsl viðskiptavina sinna.
                  <br />
                  Með PEP vaktinni er hægt að viðhafa eftirlit með stöðu viðskiptavina með tilliti til stjórnmálalegra
                  tengsla. Jafnframt er hægt að vakta viðskiptavini á móti alþjóðlegum válistum: þvingunarlistum, PEP og
                  neikvæðri umfjöllun (e. adverse media).
                </p>
                <p className="paragraph">
                  Verð eru samkvæmt{" "}
                  <Link href="/Verdskra#Gagnaleit" linkSize="18">
                    verðskrá
                  </Link>
                  . Ath. þjónustan ber eitt gjald fyrir PEP vöktun og annað fyrir vöktun á móti alþjóðlegum válistum.
                </p>
                <p>
                  Með notkun eru{" "}
                  <Link href="/pep-vaktin-skilmalar" linkSize="18" rel="noopener noreferrer">
                    skilmálar
                  </Link>{" "}
                  þjónustunnar samþykktir.
                </p>
                {!isDisabled && (
                  <div style={{ marginTop: "40px" }}>
                    <Alert
                      type={"info"}
                      headText={
                        <>
                          Tíðni keyrslna á móti válistum:
                          <span style={{ fontWeight: "400", marginLeft: "5px" }}>
                            PEP listi: Daglega | Alþjóðlegir válistar: Ársfjórðungslega
                          </span>
                        </>
                      }
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="monitoringWrapper">
              <PepWatchMonitor data={monitorData} />
            </div>
          </div>
        </div>
      </div>
      <PepWatchTableSection isDisabled={isDisabled} />
    </>
  );
};

export default PepWatch;
